import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { ExternalLink, CheckCircle2, RotateCcw, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TicketThreadView } from "@/pages/support/SupportThreadPage";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import {
    CATEGORY_LABELS, PRIORITY_LABELS, STATUS_LABELS,
} from "@/lib/supportHelpers";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return format(new Date(iso), "d MMM yyyy"); } catch { return iso; }
};

const AdminRail = ({ ticket, refresh }) => {
    if (!ticket) return null;

    const patch = async (body) => {
        try {
            await api.patch(`/admin/support/tickets/${ticket.id}`, body);
            await refresh();
            toast.success("Updated");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        }
    };

    return (
        <>
            <Card className="border-border/80 shadow-sm">
                <CardContent className="p-4 space-y-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Controls</h3>
                    <div className="space-y-2">
                        <label className="text-xs text-muted-foreground">Status</label>
                        <Select value={ticket.status} onValueChange={(v) => patch({ status: v })}>
                            <SelectTrigger data-testid="admin-rail-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs text-muted-foreground">Priority</label>
                        <Select value={ticket.priority} onValueChange={(v) => patch({ priority: v })}>
                            <SelectTrigger data-testid="admin-rail-priority"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {Object.entries(PRIORITY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs text-muted-foreground">Category</label>
                        <Select value={ticket.category} onValueChange={(v) => patch({ category: v })}>
                            <SelectTrigger data-testid="admin-rail-category"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {Object.entries(CATEGORY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-2">
                        {ticket.status !== "resolved" && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => patch({ status: "resolved" })}
                                className="flex-1"
                                data-testid="admin-rail-mark-resolved"
                            >
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                Mark resolved
                            </Button>
                        )}
                        {(ticket.status === "resolved" || ticket.status === "closed") && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => patch({ status: "open" })}
                                className="flex-1"
                                data-testid="admin-rail-reopen"
                            >
                                <RotateCcw className="h-3.5 w-3.5" />
                                Reopen
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            <Card className="border-border/80 shadow-sm">
                <CardContent className="p-4 space-y-1.5">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Requested by</h3>
                    <p className="text-sm font-medium text-foreground" data-testid="admin-rail-user-name">{ticket.user_full_name || "—"}</p>
                    <p className="text-xs text-muted-foreground select-all">{ticket.user_email}</p>
                    <p className="text-xs text-muted-foreground">Ticket opened {fmtDate(ticket.created_at)}</p>
                    <Button asChild variant="ghost" size="sm" className="-ml-2 mt-2" data-testid="admin-rail-view-user">
                        <Link to={`/admin/users/${ticket.user_id}`}>
                            <ExternalLink className="h-3.5 w-3.5" />
                            View user profile
                        </Link>
                    </Button>
                </CardContent>
            </Card>
        </>
    );
};

export default function AdminSupportThreadPage() {
    useDocumentTitle("Admin · Ticket");
    return <TicketThreadView mode="admin" rightRail={AdminRail} />;
}
